import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectionListChange } from '@angular/material/list';
import { Staff } from '@common/data-models/service/staff';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { OPTION_BLANK, OPTION_ME } from '@src/suite/common/enums/constants';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { optionContainsTerm } from '@src/suite/common/utils/util';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    selector: 'st-staff-id-list-multiple',
    templateUrl: './staff-id-list-multiple.html',
    styleUrls: ['./staff-id-list-multiple.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-staff-id-list-multiple',
    },
})
export class StaffIDListMultipleComponent extends EnpsControlComponent<string[]> {
    value = input<string[]>([]);
    maxItemsToShow = input(1, {
        transform: val => coerceNumberProperty(val, 1),
    });
    includeBlank = input(false, {
        transform: coerceBooleanProperty,
    });
    includeMe = input(false, {
        transform: coerceBooleanProperty,
    });
    selectionChange = output<{ id: string[]; state: string[] }>();

    internalValue = signal<string[]>([]);

    allUsers = computed(() => {
        const staff = Object.values(this.globalTablesService.Staff);
        if (this.includeMe()) {
            const meLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebMeOption))();
            const me = new Staff(OPTION_ME, meLabel, '', '', '', '', false, '', '', '', '', meLabel);
            staff.unshift(me);
        }
        if (this.includeBlank()) {
            const blankLabel = this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebBlankOption))();
            const blank = new Staff(OPTION_BLANK, blankLabel, '', '', '', '', false, '', '', '', '', blankLabel);
            staff.unshift(blank);
        }
        return staff;
    });

    items = computed(() => {
        if (this.asCustomField() && this.usersToShow()?.length > 0) {
            return this.usersToShow();
        }
        return this.internalValue();
    });
    usersToShow = computed(() => {
        return this.internalValue()?.slice(0, this.maxItemsToShow());
    });

    usersAsTooltip = computed(() => {
        return this.internalValue()
            ?.slice(this.maxItemsToShow())
            .map(item => this.getName(item))
            .join('\n');
    });

    searchLabel = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebSearchLabel))()}...`;
    });

    shouldShowUsersAsTooltip = computed(() => {
        return this.asCustomField() && this.internalValue()?.length > this.maxItemsToShow();
    });

    usersAsTooltipChip = computed(() => {
        return `+ ${this.internalValue()?.length - this.maxItemsToShow()} more`;
    });

    staffFilter = new FormControl('');
    filteredOptions: Observable<string[]>;

    constructor() {
        super();
        effect(
            () => {
                this.internalValue.set([...(this.value() || [])]);
            },
            { allowSignalWrites: true }
        );
    }

    displayNameByKey(key: string): string {
        return this.globalTablesService.Staff && this.globalTablesService.Staff[key] ? `${this.globalTablesService.Staff[key].GivenName} ${this.globalTablesService.Staff[key].Surname}` : '';
    }

    valueChanged() {
        if (this.isValueChanged()) {
            this.isValueChanged.set(false);
            this.changed.emit(this.internalValue());
        }
        this.staffFilter.setValue('');
    }

    getName(userId: string) {
        if (userId === OPTION_BLANK) {
            return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebBlankOption))();
        }
        if (userId === OPTION_ME) {
            return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebMeOption))();
        }
        if (!this.globalTablesService?.Staff[userId]) {
            return userId;
        }
        return `${this.globalTablesService?.Staff[userId]?.GivenName} ${this.globalTablesService?.Staff[userId]?.Surname}`;
    }

    getEmail(userId: string) {
        return this.globalTablesService?.Staff[userId]?.Email || '';
    }

    onMenuOpened() {
        super.onMenuOpened();
        this.filteredOptions = this.staffFilter.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || ''))
        );
    }

    private _filter(value: string): string[] {
        return this.allUsers()
            .filter(option => (optionContainsTerm(option.UserID, value) || optionContainsTerm(option.DisplayName, value)) && !this.internalValue()?.includes(option.UserID))
            .sort((a, b) => a.DisplayName?.localeCompare(b?.DisplayName))
            .map(item => item.UserID);
    }

    remove(fruit: string): void {
        this.isValueChanged.set(true);
        const index = this.internalValue().indexOf(fruit);

        if (index >= 0) {
            this.internalValue.set(this.internalValue().filter((_, idx) => idx !== index));
            this.valueChanged();
        }
    }

    onSelectionChange(event: MatSelectionListChange): void {
        this.isValueChanged.set(true);
        const { value } = event.options[0];
        this.internalValue.set([...this.internalValue(), value]);
        this.searchBox().nativeElement.value = '';
        this.staffFilter.setValue('');
        this.selectionChange.emit({ id: value, state: this.internalValue() });
    }
}
